// v1.27.15 — Sparkler fireworks. A full-viewport, pointer-events-none
// overlay that fires ~7 rockets over ~3.5s. Each rocket rises then
// bursts into ~16 sparks in a tri-color + gold palette. Pure CSS keyframes
// (no canvas, no image), respects prefers-reduced-motion.
//
// Auto-fires once per day on 15-Aug (Independence) and 26-Jan (Republic) —
// dedupe via localStorage. Also manually triggerable from the flag banner.

import React, { useEffect, useMemo } from "react";

const COLORS = ["#FF9933", "#FFFFFF", "#138808", "#FFD700", "#F5A623", "#FF6EC7"];
const RANDOM_SEEDS = Array.from({ length: 128 }, (_, i) => (i * 2654435761) % 1000 / 1000);
const _rnd = (i) => RANDOM_SEEDS[i % RANDOM_SEEDS.length];

function Rocket({ index, launchAtMs }) {
    // v1.27.17 — Bigger blast: more sparks, wider spread, larger particles.
    const sparks = 24;
    const startX = 8 + Math.floor(_rnd(index * 7) * 84);       // 8 – 92 %
    const burstY = 30 + Math.floor(_rnd(index * 11) * 40);     // 30 – 70 % from top
    const risePx = burstY;                                     // percentage points
    const color = COLORS[index % COLORS.length];
    const spread = 220 + Math.floor(_rnd(index * 13) * 200);   // 220 – 420 px explosion radius
    const riseDur = 750 + Math.floor(_rnd(index * 5) * 260);   // 750 – 1010 ms

    return (
        <div
            className="pointer-events-none absolute"
            style={{
                left: `${startX}%`,
                bottom: 0,
                animation: `spark-rise ${riseDur}ms ease-out ${launchAtMs}ms forwards`,
                ["--rise" ]: `${risePx}vh`,
            }}
        >
            {/* Rising streak — thicker, longer, glowier */}
            <div
                className="pointer-events-none absolute -translate-x-1/2"
                style={{
                    width: 5,
                    height: 42,
                    borderRadius: 3,
                    background: `linear-gradient(180deg, ${color}, transparent)`,
                    filter: `drop-shadow(0 0 14px ${color}) drop-shadow(0 0 26px ${color})`,
                    animation: `spark-fade ${riseDur}ms linear ${launchAtMs}ms forwards`,
                }}
            />
            {/* Bright central flash at burst */}
            <div
                className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{
                    width: 40, height: 40, top: 0, left: 0, opacity: 0,
                    background: `radial-gradient(closest-side, #fff 0%, ${color} 45%, transparent 70%)`,
                    filter: `drop-shadow(0 0 40px ${color})`,
                    animation: `spark-flash 700ms ease-out ${launchAtMs + riseDur}ms forwards`,
                }}
            />
            {/* Burst sparks */}
            {Array.from({ length: sparks }).map((_, i) => {
                const angle = (360 / sparks) * i + _rnd(index * 100 + i) * 10;
                const dist = spread * (0.7 + _rnd(index * 200 + i) * 0.55);
                const sc = COLORS[(index + i) % COLORS.length];
                const size = 10 + Math.floor(_rnd(index * 300 + i) * 8);   // 10 – 17 px
                return (
                    <div
                        key={i}
                        className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2"
                        style={{
                            width: size,
                            height: size,
                            borderRadius: "50%",
                            background: sc,
                            filter: `drop-shadow(0 0 14px ${sc}) drop-shadow(0 0 26px ${sc})`,
                            top: 0,
                            left: 0,
                            opacity: 0,
                            animation: `spark-burst 2000ms cubic-bezier(.12,.72,.18,1) ${launchAtMs + riseDur}ms forwards`,
                            ["--tx"]: `${Math.cos((angle * Math.PI) / 180) * dist}px`,
                            ["--ty"]: `${Math.sin((angle * Math.PI) / 180) * dist}px`,
                        }}
                    />
                );
            })}
        </div>
    );
}

export default function SparklerFireworks({ open, onClose, rockets = 12 }) {
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => { onClose?.(); }, 4600);
        return () => clearTimeout(t);
    }, [open, onClose]);
    const launches = useMemo(() => Array.from({ length: rockets }, (_, i) => i * 220 + Math.floor(_rnd(i * 3) * 260)), [rockets]);
    if (!open) return null;
    return (
        <div className="pointer-events-none fixed inset-0 z-[120] overflow-hidden" data-testid="sparkler-fireworks">
            {launches.map((delay, i) => (<Rocket key={i} index={i} launchAtMs={delay} />))}
            <style>{`
                @keyframes spark-rise {
                    0%   { transform: translateY(0); }
                    100% { transform: translateY(calc(-1 * var(--rise, 40vh))); }
                }
                @keyframes spark-fade {
                    0%   { opacity: 1; }
                    100% { opacity: 0; }
                }
                @keyframes spark-burst {
                    0%   { opacity: 1; transform: translate(-50%, -50%) scale(0.4); }
                    18%  { opacity: 1; transform: translate(calc(-50% + var(--tx) * 0.65), calc(-50% + var(--ty) * 0.65)) scale(1.4); }
                    55%  { opacity: 0.95; transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty))) scale(1); }
                    100% { opacity: 0; transform: translate(calc(-50% + var(--tx) * 1.2), calc(-50% + var(--ty) * 1.2 + 60px)) scale(0.55); }
                }
                @keyframes spark-flash {
                    0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.4); }
                    30%  { opacity: 1; transform: translate(-50%, -50%) scale(1.8); }
                    100% { opacity: 0; transform: translate(-50%, -50%) scale(2.6); }
                }
                @media (prefers-reduced-motion: reduce) {
                    [data-testid="sparkler-fireworks"] * { animation-duration: 0s !important; }
                }
            `}</style>
        </div>
    );
}

// Utility: returns true if today is a national-day (15-Aug / 26-Jan).
export function isNationalDayToday(d = new Date()) {
    const m = d.getMonth() + 1;
    const day = d.getDate();
    return (m === 8 && day === 15) || (m === 1 && day === 26);
}
